(() => {
  if (customElements.get('recent-purchase-notification')) return;

  class RecentPurchaseNotification extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.card = this.querySelector('[data-rpn-card]');
      this.status = this.querySelector('[data-rpn-status]');
      this.closeButton = this.querySelector('[data-rpn-close]');
      this.items = this.readItems();
      this.index = -1;
      this.abortController = new AbortController();
      this.closeButton?.addEventListener('click', () => this.dismiss(), { signal: this.abortController.signal });
      this.mount();
      this.start();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.showTimer);
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.nextTimer);
    }

    number(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    readItems() {
      try {
        const node = this.querySelector('[data-rpn-products]');
        const value = JSON.parse(node?.textContent || '[]');
        return Array.isArray(value) ? value.filter((item) => item && item.title) : [];
      } catch (error) {
        console.warn('Recent purchase notification: invalid fallback product data.', error);
        return [];
      }
    }

    mount() {
      if (this.dataset.displayMode !== 'inline') return;
      const selector = this.dataset.mountSelector?.trim();
      if (!selector) return;
      let target;
      try { target = document.querySelector(selector); }
      catch (error) { console.warn('Recent purchase notification: invalid mount selector.', error); return; }
      if (!target || target === this || this.contains(target)) return;
      const position = this.dataset.mountPosition || 'afterend';
      if (position === 'inside-start') target.prepend(this);
      else if (position === 'inside-end') target.append(this);
      else target.insertAdjacentElement(position, this);
    }

    async start() {
      if (this.isDismissed()) return this.setAttribute('hidden', '');
      if (this.dataset.sourceMode !== 'configured') {
        const remoteItems = await this.fetchItems();
        if (remoteItems.length) this.items = remoteItems;
        else if (this.dataset.sourceMode === 'api') this.items = [];
      }
      if (!this.items.length) return this.setAttribute('hidden', '');
      this.removeAttribute('hidden');
      this.showTimer = window.setTimeout(() => this.showNext(), this.number('initialDelay', 3000));
    }

    async fetchItems() {
      const endpoint = this.dataset.endpoint?.trim();
      if (!endpoint) return [];
      try {
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, credentials: this.dataset.sendCredentials === 'true' ? 'same-origin' : 'omit' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const collection = this.readPath(payload, this.dataset.itemsPath || 'items');
        if (!Array.isArray(collection)) return [];
        return collection.map((item) => this.mapRemoteItem(item)).filter((item) => item.title);
      } catch (error) {
        console.warn('Recent purchase notification: data request failed.', error);
        return [];
      }
    }

    readPath(value, path) {
      if (!path) return value;
      return path.split('.').filter(Boolean).reduce((current, key) => current?.[key], value);
    }

    mapRemoteItem(item) {
      const field = (name, fallback = '') => this.readPath(item, this.dataset[name] || '') ?? fallback;
      return { title: String(field('titlePath')), url: String(field('urlPath', '#')), image: String(field('imagePath')), buyer: String(field('buyerPath')), location: String(field('locationPath')), time: String(field('timePath')), variant: String(field('variantPath')) };
    }

    showNext() {
      if (!this.items.length) return;
      this.index = (this.index + 1) % this.items.length;
      this.render(this.items[this.index]);
      this.classList.add('is-visible');
      this.hideTimer = window.setTimeout(() => this.hide(), this.number('visibleDuration', 6000));
    }

    hide() {
      this.classList.remove('is-visible');
      this.nextTimer = window.setTimeout(() => this.showNext(), this.number('interval', 5000));
    }

    render(item) {
      const setText = (selector, value) => { const node = this.querySelector(selector); if (node) { node.textContent = value || ''; node.hidden = !value; } };
      const title = this.querySelector('[data-rpn-title]');
      const image = this.querySelector('[data-rpn-image]');
      const media = this.querySelector('[data-rpn-media]');
      if (title) { title.textContent = item.title; title.href = item.url || '#'; }
      if (media) media.href = item.url || '#';
      if (image) { image.src = item.image || ''; image.alt = item.title; image.hidden = !item.image; }
      setText('[data-rpn-buyer]', this.format(this.dataset.buyerTemplate, item));
      setText('[data-rpn-location]', item.location);
      setText('[data-rpn-time]', item.time);
      setText('[data-rpn-variant]', item.variant);
      const announcement = this.format(this.dataset.announcementTemplate, item);
      if (this.status) this.status.textContent = announcement;
    }

    format(template = '', item) {
      return template.replaceAll('[buyer]', item.buyer || '').replaceAll('[location]', item.location || '').replaceAll('[product]', item.title || '').replaceAll('[time]', item.time || '').replaceAll('[variant]', item.variant || '').replace(/\s{2,}/g, ' ').trim();
    }

    dismiss() {
      this.classList.remove('is-visible');
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.nextTimer);
      if (this.dataset.rememberDismissal === 'true') {
        try { localStorage.setItem(this.storageKey(), String(Date.now())); } catch (_) {}
      }
      window.setTimeout(() => this.setAttribute('hidden', ''), 350);
    }

    isDismissed() {
      if (this.dataset.rememberDismissal !== 'true') return false;
      try {
        const timestamp = Number(localStorage.getItem(this.storageKey()));
        return timestamp > 0 && Date.now() - timestamp < this.number('dismissDuration', 86400000);
      } catch (_) { return false; }
    }

    storageKey() { return `recent-purchase-notification:${this.dataset.storageKey || 'default'}`; }
  }

  customElements.define('recent-purchase-notification', RecentPurchaseNotification);
})();
